import React, { useState } from 'react';
import {
  PartialQuestionnaire,
  Question,
  QuestionnaireConfiguration,
  QuestionnaireQuestions,
  RoutesObject,
  QuestionnaireError,
  Rule,
} from '../models';
import { PageNotFound, Screen } from '../components';
import { getQuestionComponent } from '../utils/getQuestionComponent';
import { getQuestionObject } from '../utils/getQuestionObject';
import { runValidation } from '../validators';
import { ProgressWrapper } from '../components/ProgressWrapper';
import { calculateFields, CalculationFields } from '../utils/calculateFields';
import { HistoryInfoProps } from './QuestionnaireRouter';

export type QuestionnaireProps<
  Questionnaire extends QuestionnaireQuestions,
  GroupId = unknown,
  ExtendedComponentTypes = unknown
> = {
  featureName: string;
  questionnaire: {
    routes?: RoutesObject<Questionnaire>;
    components: PartialQuestionnaire<
      Questionnaire,
      GroupId,
      ExtendedComponentTypes
    >;
    rules?: Rule<Questionnaire>[];
    calculatedFields?: CalculationFields<Questionnaire>;
  };
  questionnaireAnswers: Partial<Questionnaire>;
  onAnswer: <QuestionId extends keyof Questionnaire>(
    questionId: QuestionId,
    answer: Questionnaire[QuestionId],
    additionalData?: Partial<Questionnaire>,
    calculatedFields?: Record<string, number>
  ) => void;
  questionnaireUrl?: string;
  sendQuestionnaireError?: (error: QuestionnaireError) => void;
  configuration?: QuestionnaireConfiguration<
    Questionnaire,
    ExtendedComponentTypes
  >;
  questionId: keyof Questionnaire;
  getNextQuestionId?: (
    answers: Partial<Questionnaire>,
    questionId: keyof Questionnaire
  ) => keyof Questionnaire | null;
  onSubmitAnswer?: (
    currentState: Partial<Questionnaire>,
    questionId: keyof Questionnaire,
    historyInfo?: HistoryInfoProps
  ) => void;
  navigateTo?: (path: string) => void;
};

export const Questionnaire = <
  Questionnaire extends QuestionnaireQuestions,
  GroupId,
  ExtendedComponentTypes
>({
  questionnaire,
  questionnaireAnswers,
  onAnswer,
  featureName,
  sendQuestionnaireError,
  configuration = { continueButtonText: 'Continue' },
  questionId,
  onSubmitAnswer,
  navigateTo,
}: QuestionnaireProps<Questionnaire, GroupId, ExtendedComponentTypes>) => {
  const onSubmitValue = async <QuestionId extends keyof Questionnaire>(
    questionObject: Question<Questionnaire, GroupId, ExtendedComponentTypes>,
    currentAnswer: Questionnaire[QuestionId],
    additionalData?: Partial<Questionnaire>,
    historyInfo?: HistoryInfoProps
  ) => {
    const answer = {
      [questionObject.id]: currentAnswer,
    };

    const data = { ...questionnaireAnswers, ...answer };
    const calculatedAnswers = questionnaire.calculatedFields
      ? calculateFields(questionnaire.calculatedFields, data)
      : undefined;

    if (calculatedAnswers) {
      onAnswer(
        questionObject.id,
        currentAnswer,
        additionalData,
        calculatedAnswers
      );

      onSubmitAnswer?.(
        {
          ...questionnaireAnswers,
          ...answer,
          ...calculatedAnswers,
          ...additionalData,
        },
        questionObject.id,
        historyInfo
      );
    } else {
      onAnswer(questionObject.id, currentAnswer, additionalData);

      onSubmitAnswer?.(
        { ...questionnaireAnswers, ...answer, ...additionalData },
        questionObject.id,
        historyInfo
      );
    }
  };

  const [validForSubmission, setValidForSubmission] = useState(true);
  const [validationError, setValidationError] = useState<{
    type: 'Invalid' | 'Error' | 'Info';
    msg: string;
  }>();

  const questionObject = getQuestionObject(
    questionnaire.components,
    questionId
  );

  if (!questionObject) return <PageNotFound />;

  const { type, validations } = questionObject;

  const handleSubmitValue = <QuestionId extends keyof Questionnaire>(
    answer: Questionnaire[QuestionId],
    additionalData?: Partial<Questionnaire>,
    historyInfo?: HistoryInfoProps
  ) => {
    if (answer !== undefined) {
      const validationResult = runValidation(
        validations ?? [],
        answer,
        questionnaireAnswers
      );
      if (validationResult.type === 'Valid') {
        setValidationError(undefined);
        onSubmitValue(questionObject, answer, additionalData, historyInfo);
      } else {
        setValidationError(validationResult);
      }
    }
  };

  const newProps = {
    key: String(questionId),
    featureName,
    questionnaireAnswers,
    configuration,
    onSubmitValue: handleSubmitValue,
    question: questionObject.screen?.question,
    error: validationError,
    ...(sendQuestionnaireError ? { sendQuestionnaireError } : {}),
  };

  const Component = getQuestionComponent<ExtendedComponentTypes>(
    type,
    configuration.components
  );

  if (!Component || React.isValidElement(Component)) {
    throw new Error(
      `[QUESTIONNAIRE_FRAMEWORK]: invalid component used in ${featureName} - ${String(
        questionId
      )}`
    );
  }

  const props = {
    ...newProps,
    ...questionObject.screen,
    value: questionnaireAnswers[questionId] ?? questionObject.props?.value,
  };

  const questionScreen = (
    <Screen
      {...props}
      onSubmitValue={handleSubmitValue}
      configuration={{ ...props.configuration, ...questionObject.screen }}
      validForSubmission={validForSubmission}
    >
      <Component
        {...props}
        {...questionObject.props}
        navigateTo={navigateTo}
        routes={questionnaire.routes}
        onSubmitValue={handleSubmitValue}
        setValidForSubmission={setValidForSubmission}
      />
    </Screen>
  );

  if (configuration.progressBar) {
    return (
      <ProgressWrapper
        onClick={configuration.progressBar.onProgressClick}
        questionId={questionId}
        questionnaireAnswers={questionnaireAnswers}
        rules={questionnaire.rules}
        questions={questionnaire.components}
        questionGroupsMapping={configuration.progressBar.questionGroupsMapping}
        questionnaireIdMapping={
          configuration.progressBar.questionnaireIdMapping
        }
      >
        {questionScreen}
      </ProgressWrapper>
    );
  }

  return questionScreen;
};
